import { useNavigate } from 'react-router-dom'
import { useState, useCallback } from 'react'
import { useAppSelector } from '../hooks/useAppDispatch'
import { useLoginPrompt } from '../context/LoginPromptContext'
import UserHoverCard from './ui/UserHoverCard'
import { timeAgoShort } from '../lib/avatar'
import { PINNING_ENABLED, PIN_COLOR } from '../lib/config'
import Avatar from './ui/Avatar'
import ShareModal from './ui/ShareModal'

export interface DiscussionCardProps {
  id: string
  title: string
  contentPreview: string
  author: { id: string; displayName: string; avatarUrl?: string | null; flair?: string | null }
  categories?: { id: string; name: string; slug: string; color: string | null; icon: string | null }[]
  tags: string[]
  viewCount: number
  commentCount: number
  voteScore: number
  createdAt: string
  isPinned?: boolean
}

export default function DiscussionCard({
  id, title, contentPreview, author, categories,
  tags, viewCount, commentCount, voteScore,
  createdAt, isPinned,
}: DiscussionCardProps) {
  const { isAuthenticated } = useAppSelector((s) => s.auth)
  const currentUser = useAppSelector((s) => s.auth.user)
  const currentUserId = currentUser?.id
  const avatarUrl = author.id === currentUserId
    ? (currentUser?.avatarUrl || author.avatarUrl)
    : author.avatarUrl

  const navigate = useNavigate()
  const [shareOpen, setShareOpen] = useState(false)

  const { trigger: promptLogin } = useLoginPrompt()

  const discussionUrl = `${window.location.origin}/discussions/${id}`

  const openShare = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isAuthenticated) {
      promptLogin('Sign in to share this discussion')
      return
    }
    setShareOpen(true)
  }, [isAuthenticated, promptLogin])

  return (
    <>
      <div
        onClick={() => void navigate(`/discussions/${id}`, {
          state: {
            optimistic: {
              title,
              content: contentPreview,
              tags: tags.map(t => ({ slug: t, name: t, id: t })),
              author,
              categories,
              voteScore,
              commentCount,
              viewCount,
              createdAt,
            },
          },
        })}
        style={{
          background: 'var(--bg-card)',
          border: (PINNING_ENABLED && isPinned) ? '1px solid var(--accent-primary)' : '1px solid var(--border)',
          borderRadius: '12px',
          padding: '1.25rem',
          marginBottom: '0.75rem',
          cursor: 'pointer',
          transition: 'border-color 0.15s, box-shadow 0.15s',
          position: 'relative',
        }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border-hover)'
            ;(e.currentTarget as HTMLDivElement).style.boxShadow = 'var(--shadow-card)'
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLDivElement).style.borderColor = (PINNING_ENABLED && isPinned) ? 'var(--accent-primary)' : 'var(--border)'
            ;(e.currentTarget as HTMLDivElement).style.boxShadow = 'none'
          }}
        >
          {/* Pin indicator */}
          {PINNING_ENABLED && isPinned && (
            <div style={{ position: 'absolute', top: '1rem', right: '1rem', color: PIN_COLOR, fontSize: '1rem' }}>📌</div>
          )}

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <UserHoverCard userId={author.id} currentUserId={currentUserId} displayName={author.displayName} avatarUrl={avatarUrl}>
              <Avatar name={author.displayName} avatarUrl={avatarUrl} size={36} />
            </UserHoverCard>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.25rem', lineHeight: 1.4 }}>{title}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <UserHoverCard userId={author.id} currentUserId={currentUserId} displayName={author.displayName} avatarUrl={avatarUrl}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textDecoration: 'none' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent-primary)')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
                  >
                    by {author.displayName}
                  </span>
                </UserHoverCard>
                {author.flair && (
                  <span style={{ fontSize: '0.68rem', background: 'rgba(124,58,237,0.08)', color: 'var(--accent-primary)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: '20px', padding: '0.08rem 0.45rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {author.flair}
                  </span>
                )}
                {categories && categories.length === 0 && (
                  <>
                    <span style={{ color: 'var(--border)', fontSize: '0.75rem' }}>·</span>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
                      fontSize: '0.72rem', fontWeight: 500,
                      color: 'var(--text-muted)',
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border)',
                      padding: '0.1rem 0.4rem', borderRadius: '6px',
                    }}>
                      ⏳ Classifying...
                    </span>
                  </>
                )}
                {categories && categories.length > 0 && (
                  <>
                    <span style={{ color: 'var(--border)', fontSize: '0.75rem' }}>·</span>
                    {categories.map((cat) => (
                      <span
                        key={cat.id}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); void navigate(`/?categorySlugs=${encodeURIComponent(cat.slug)}`) }}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
                          fontSize: '0.72rem', fontWeight: 500,
                          color: cat.color ?? 'var(--accent-primary)',
                          background: `${cat.color ?? 'var(--accent-primary)'}18`,
                          border: `1px solid ${cat.color ?? 'var(--accent-primary)'}44`,
                          padding: '0.1rem 0.4rem', borderRadius: '6px',
                          cursor: 'pointer', transition: 'opacity 0.15s',
                        }}
                        onMouseEnter={e => (e.currentTarget as HTMLSpanElement).style.opacity = '0.75'}
                        onMouseLeave={e => (e.currentTarget as HTMLSpanElement).style.opacity = '1'}
                        title={`Filter by ${cat.name}`}
                      >
                        {cat.icon && <span style={{ fontSize: '0.68rem' }}>{cat.icon}</span>}
                        {cat.name}
                      </span>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              {tags.map((tag) => (
                <span
                  key={tag}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); void navigate(`/search?tag=${encodeURIComponent(tag)}`) }}
                  style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', padding: '0.15rem 0.5rem', borderRadius: '8px', fontSize: '0.72rem', border: '1px solid var(--border)', cursor: 'pointer', transition: 'all 0.15s' }}
                  onMouseEnter={(e) => { const el = e.currentTarget as HTMLSpanElement; el.style.color = 'var(--accent-primary)'; el.style.borderColor = 'rgba(124,58,237,0.4)'; el.style.background = 'rgba(124,58,237,0.08)' }}
                  onMouseLeave={(e) => { const el = e.currentTarget as HTMLSpanElement; el.style.color = 'var(--text-muted)'; el.style.borderColor = 'var(--border)'; el.style.background = 'var(--bg-elevated)' }}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Content preview */}
          <p style={{ margin: '0 0 0.75rem 0', color: 'var(--text-muted)', fontSize: '0.875rem', lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {contentPreview?.length > 150 ? contentPreview.slice(0, 150) + '...' : contentPreview}
          </p>

          {/* Footer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Stat icon="💬" value={commentCount} />
              <Stat icon="👁️" value={viewCount} />
              <Stat icon="⬆️" value={voteScore} />
              <Stat icon="🕐" value={timeAgoShort(createdAt)} />
            </div>
            <button
              onClick={openShare}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.3rem',
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)', fontSize: '0.78rem',
                padding: '0.2rem 0.4rem', borderRadius: '6px', transition: 'color 0.15s',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--accent-primary)' }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)' }}
            >
              <span style={{ fontSize: '0.8rem' }}>🔗</span>
              <span>Share</span>
            </button>
          </div>
        </div>

      {/* Share modal rendered via portal — completely outside the card */}
      {shareOpen && (
        <ShareModal
          title={title}
          contentPreview={contentPreview}
          url={discussionUrl}
          onClose={() => setShareOpen(false)}
        />
      )}
    </>
  )
}

const Stat = ({ icon, value }: { icon: string; value: string | number }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
    <span style={{ fontSize: '0.75rem' }}>{icon}</span>
    <span>{value}</span>
  </span>
)
